import { inject, Injectable } from "@angular/core";
import { BehaviorSubject, combineLatest, combineLatestWith, debounceTime, distinct, firstValueFrom, forkJoin, map, merge, Observable, switchMap, tap, withLatestFrom } from "rxjs";
import { UsersService } from "../../app/services/user.service";
import { CommunityAuthService } from "../../app/services/community-auth.service";
import { User } from "../../app/models/user";
import { AccessibilityPreferences, UIPreference } from "../../app/models/ui-preferences";

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly storageKey = 'darkMode';
  private isDarkModeSubject: BehaviorSubject<boolean>;
  private darkModeToggledSubject: BehaviorSubject<boolean | null>;

  darkModeGuess = false;
  isDarkMode$;
  uiPreference: UIPreference | undefined;
  userService = inject(UsersService);
  authService = inject(CommunityAuthService);
  currentUser: User | undefined;
  deviceLightDarkSetting = "unknown";
  darkModeToggled$: Observable<boolean | null>;

  constructor() {
    // Load saved preference, default false (light mode)
    // const saved = localStorage.getItem(this.storageKey);
    // const initialValue = saved ? JSON.parse(saved) : false;

    this.darkModeGuess = this.decodeUserPrefs(this.userService.currentUser)?.accessibility?.isDarkMode ?? false;
    this.applyTheme(this.darkModeGuess);
    this.isDarkModeSubject = new BehaviorSubject<boolean>(this.darkModeGuess);
    this.darkModeToggledSubject = new BehaviorSubject<boolean | null>(this.getSessionDarkMode());
    this.darkModeToggled$ = this.darkModeToggledSubject.pipe(tap(t => { if (t != null) { sessionStorage["isDarkMode"] = t; } console.log("setting session", t) }), debounceTime(100));
    this.isDarkMode$ = this.isDarkModeSubject.pipe(debounceTime(100));
    this.isDarkMode$
      .subscribe(isDarkMode => {
        this.applyTheme(isDarkMode);
      });
    this.authService.isAuthenticated$.subscribe(isAuth => { if (!isAuth) { sessionStorage.removeItem('isDarkMode'); } });
    // Apply theme on startup
    this.authService.isImpersonated
      .pipe(
        distinct(),
        debounceTime(200),
        switchMap(_ => this.userService.getCurrentUser().pipe(tap(_ => console.log("emitting gcu")))),
        combineLatestWith(this.userService.preferencesUpdated$.pipe(tap(_ => console.log("emitting pref"))), this.darkModeToggled$.pipe(tap(_ => console.log("emitting tog"))))
      ).subscribe(async ([user, uipref, isDarkMode]) => {
        // console.log(user, isDarkMode);
        this.currentUser = user;
        this.uiPreference = uipref ?? this.decodeUserPrefs(user);
        console.log("Setting preferences...", this.uiPreference.accessibility?.themePersistence);
        switch (this.uiPreference?.accessibility?.themePersistence) {
          case 'api':
            if (!this.uiPreference!.accessibility)
              this.uiPreference!.accessibility = new AccessibilityPreferences();
            if (isDarkMode != null && isDarkMode != this.uiPreference.accessibility.isDarkMode) {
              this.uiPreference!.accessibility!.isDarkMode = isDarkMode;
              await firstValueFrom(this.userService.savePreference(this.uiPreference!, this.currentUser!.id));
            }
            console.log("Emitting from API", this.uiPreference?.accessibility?.isDarkMode ?? false);
            this.isDarkModeSubject.next(this.uiPreference?.accessibility?.isDarkMode ?? false);
            break;
          case 'local':
            if (isDarkMode != null)
              localStorage["accessibility.isDarkMode"] = isDarkMode;
            console.log("Emitting from local", isDarkMode ?? false);
            this.isDarkModeSubject.next(isDarkMode ?? false);
            break;
          default:
            const sessDark = this.getSessionDarkMode();
            const isDark = sessDark != null ? sessDark : this.deviceLightDarkSetting == 'dark';
            console.log("Emitting default", isDark, this.uiPreference.accessibility?.themePersistence)
            this.isDarkModeSubject.next(isDark);
        }
      });

    const darkQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const lightQuery = window.matchMedia('(prefers-color-scheme: light)');

    const updateTheme = () => {
      let newDeviceLightDarkSetting = null;
      if (darkQuery.matches) newDeviceLightDarkSetting = 'dark';
      else if (lightQuery.matches) newDeviceLightDarkSetting = 'light';
      else newDeviceLightDarkSetting = 'no-preference';
      if (newDeviceLightDarkSetting != this.deviceLightDarkSetting) {
        sessionStorage.removeItem("isDarkMode");
        this.deviceLightDarkSetting = newDeviceLightDarkSetting;
      }

      if (! this.uiPreference?.accessibility?.themePersistence) {
        // this.applyTheme(this.deviceLightDarkSetting == 'dark');
        this.isDarkModeSubject.next(this.deviceLightDarkSetting == 'dark');
      }
    };

    // Run initially
    updateTheme();

    // Listen for changes
    darkQuery.addEventListener('change', updateTheme);
    lightQuery.addEventListener('change', updateTheme);
  }

  getSessionDarkMode() {
    return sessionStorage["isDarkMode"] != null ? sessionStorage["isDarkMode"] == 'true' : null
  }

  toggleTheme(isDark: boolean): void {
    this.darkModeToggledSubject.next(isDark);
  }

  decodeUserPrefs(user?: User) {
    return user?.userUIPreference?.data ? <UIPreference>JSON.parse(user!.userUIPreference!.data) : new UIPreference();
  }

  private applyTheme(isDark: boolean): void {
    console.log("Theme applied", isDark);
    const doc = document.documentElement;
    if (isDark) {
      doc.classList.add('dark-theme');
      doc.classList.remove('light-theme');
    } else {
      doc.classList.add('light-theme');
      doc.classList.remove('dark-theme');
    }
  }

  // private darkModeSubject = new BehaviorSubject<boolean>(false);
  // isDarkMode$ = this.darkModeSubject.asObservable();

  // toggleTheme(isDark: boolean): void {
  //   const body = document.body;
  //   body.classList.remove('light-theme', 'dark-theme');
  //   body.classList.add(isDark ? 'dark-theme' : 'light-theme');

  //   this.darkModeSubject.next(isDark);
  // }

  // isDark(): boolean {
  //   return this.darkModeSubject.value;
  // }
}
